import React, { useEffect, useRef } from "react";
import { EXAM_CONFIG } from "../utils/AssessmentStrings";
import { CameraRecorderProps } from "../interface/camera";
import { saveMedia } from "../utils/AssessmentStorage";

/** Default fallback capture interval if none passed via props. */
const DEFAULT_CAPTURE_INTERVAL = EXAM_CONFIG.CAMERA_CAPTURE_INTERVAL_MS;

const CameraRecorder: React.FC<CameraRecorderProps> = ({
  onPermissionDenied,
  onStream,
  captureInterval,
  isActive,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const intervalRef = useRef<number | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);

  // Effective interval either from prop or fallback constant
  const currentInterval = captureInterval ?? DEFAULT_CAPTURE_INTERVAL;

  // State to track when camera is fully warm and ready to stream/record
  const [isCameraReady, setIsCameraReady] = React.useState(false);

  // Stable refs to prevent re-triggering the primary camera mounting effect
  // when transient values fluctuate.
  const onStreamRef = useRef(onStream);
  const isActiveRef = useRef(isActive);
  useEffect(() => {
    onStreamRef.current = onStream;
    isActiveRef.current = isActive;
  }, [onStream, isActive]);

  // Main setup Effect for camera and microphone initialization
  useEffect(() => {
    let active = true;

    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: "user",
          },
          audio: true,
        });

        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;

        onStreamRef.current?.(stream);

        video.addEventListener(
          "playing",
          () => {
            if (!active) return;
            // Warm up delay
            setTimeout(() => {
              if (active) setIsCameraReady(true);
            }, 1500);
          },
          { once: true },
        );

        // Setup Audio Recording
        const audioTracks = stream.getAudioTracks();
        if (audioTracks.length > 0) {
          const audioStream = new MediaStream(audioTracks);
          const startAudioChunk = () => {
            if (!active) return;
            try {
              const mimeTypes = [
                "audio/webm;codecs=opus",
                "audio/webm",
                "audio/mp4",
                "audio/aac",
                "audio/wav",
              ];
              const supportedType = mimeTypes.find((type) =>
                MediaRecorder.isTypeSupported(type),
              );

              const mediaRecorder = new MediaRecorder(
                audioStream,
                supportedType ? { mimeType: supportedType } : undefined,
              );
              mediaRecorderRef.current = mediaRecorder;
              const chunks: Blob[] = [];

              mediaRecorder.ondataavailable = (e) => {
                if (e.data && e.data.size > 0 && active) chunks.push(e.data);
              };

              mediaRecorder.onstop = () => {
                if (chunks.length > 0 && active) {
                  const blob = new Blob(chunks, {
                    type: mediaRecorder.mimeType || "audio/webm",
                  });
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    if (isActiveRef.current) saveAudio(reader.result as string);
                  };
                  reader.readAsDataURL(blob);
                }
                if (active) startAudioChunk();
              };

              mediaRecorder.start();

              // Audio stops after its internal cycle to save chunks
              setTimeout(() => {
                if (active && mediaRecorder.state !== "inactive")
                  mediaRecorder.stop();
              }, DEFAULT_CAPTURE_INTERVAL);
            } catch (err) {
              console.warn("Audio recording failed to initialize:", err);
            }
          };
          startAudioChunk();
        }
      } catch (err) {
        console.error("Media device error:", err);
        if (active) onPermissionDenied();
      }
    };

    startCamera();

    return () => {
      active = false;
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [onPermissionDenied]);

  /** Draws the current video frame onto the canvas and saves it to localStorage. */
  /**
   * Periodically draws the current video frame to a hidden canvas
   * and converts it to a base64 string to be stored securely.
   */
  const captureImage = React.useCallback(() => {
    if (!isActive) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    // readyState must be >= 2 (HAVE_CURRENT_DATA) before we can draw a real frame.
    if (
      video.readyState < 2 ||
      video.videoWidth === 0 ||
      video.videoHeight === 0
    ) {
      console.warn("Video not ready, skipping capture");
      return;
    }

    // Match canvas dimensions to the native video resolution for full-quality captures.
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/webp", 0.85);

    // Sanity check: a blank/black WebP at this quality is always very small.
    // A real face image with meaningful content will be well above 5 KB.
    if (dataUrl.length < 5000) {
      console.warn("Capture appears blank, skipping save");
      return;
    }

    saveImage(dataUrl);
  }, [isActive]);

  // Dedicated Effect for handling periodic image captures.
  // It remounts the interval automatically if currentInterval (prop) changes.
  useEffect(() => {
    if (!isCameraReady || !isActive) return;

    // Run an immediate capture when mounting/changing interval
    captureImage();

    const intervalId = window.setInterval(captureImage, currentInterval);
    intervalRef.current = intervalId;

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isCameraReady, currentInterval, isActive, captureImage]);

  /** Appends a captured frame (base64 WebP) with its timestamp to IndexedDB. */
  const saveImage = (base64Image: string) => {
    saveMedia("examImages", {
      timestamp: new Date().toISOString(),
      image: base64Image,
    });
    console.log("Image captured and saved to IndexedDB");
  };

  /** Appends a recorded audio chunk (base64) with its timestamp to IndexedDB. */
  const saveAudio = (base64Audio: string) => {
    saveMedia("examAudio", {
      timestamp: new Date().toISOString(),
      audio: base64Audio,
    });
    console.log("Audio chunk saved to IndexedDB");
  };

  // ── Rendering ──────────────────────────────────────────────────────────────
  // Chrome throttles video frame decoding when opacity is near-zero.
  // Solution: render the video at full opacity inside a 1×1 px clipped container.
  // The browser compositor sees it as "visible" and keeps decoding frames,
  // but the student effectively sees nothing (1 px is imperceptible).
  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        bottom: 0,
        right: 0,
        width: "1px",
        height: "1px",
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: -1,
      }}
    >
      {/* Video source — kept in DOM at full opacity so the browser renders frames. */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{ width: "160px", height: "120px", display: "block" }}
      />

      {/* Off-screen canvas used only for drawing & exporting frames — never shown. */}
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
};

export default CameraRecorder;
